import { MaybeLinkProps } from '@cardboard-ui/Link';
import {
  Box,
  HStack,
  Link,
  LinkBox,
  LinkOverlay,
  SkeletonCircle,
  SkeletonText,
  Text,
} from '@cardboard-ui/react';
import { t } from '@lingui/macro';
import { EventCard } from 'apps/TenantApp/screens/Event/components/EventCard';
import { SpaceCard } from 'apps/TenantApp/screens/OrganisationSettings/screens/SpacesSettings/components/SpaceCard';
import { Card } from 'components/cards/Card';
import FolderItem from 'components/Folder/FolderItem';
import { InfoIcon, WarningIcon } from 'components/icons';
import React, { Suspense } from 'react';
import { ErrorBoundary } from 'utils/errors';
import { graphql, useFragment, useSafeRelay } from 'utils/graphClient';
import { AnnouncementAttachmentCard } from './components/AnnouncementAttachmentCard';
import { MinimalAnnouncementCard } from './components/MinimalAnnouncementCard';
import { ItemCard_item$key } from './__generated__/ItemCard_item.graphql';
import { ItemCard_item_Query } from './__generated__/ItemCard_item_Query.graphql';

const itemCardFragment = graphql`
  fragment ItemCard_item on Node {
    id
    __typename
    ...useItemHandleFragment
    ...ItemMenu_node
    ... on Event {
      name
      ...EventCard_event
    }
    ... on Folder {
      name
    }
    ... on FolderItem {
      name
      content {
        __typename
        ... on File {
          mimeType
        }
        ... on Page {
          id
        }
      }
    }
    ... on Space {
      ...SpaceCard_space
    }
    ... on Announcement {
      ...MinimalAnnouncementCard_announcement
    }
    ... on AnnouncementAttachment {
      ...AnnouncementAttachmentCard_details
    }
  }
`;

const itemCardQuery = graphql`
  query ItemCard_item_Query($itemId: ID!) {
    node(id: $itemId) {
      __typename
      ...ItemCard_item
    }
  }
`;

interface SelfLoadingItemCardProps {
  itemId: string;
  item?: never;
}

interface PreloadedItemCardProps {
  itemId?: never;
  item: ItemCard_item$key;
}

interface ItemCardWithDeletedLinkProps {
  itemId?: never;
  item?: null;
}

type ItemCardProps =
  | PreloadedItemCardProps
  | SelfLoadingItemCardProps
  | ItemCardWithDeletedLinkProps;

export const ItemCard: React.FC<ItemCardProps> = ({ item, itemId }) => {
  if (item) {
    return <PreloadedItemCard item={item} />;
  }

  if (itemId) {
    return (
      <ErrorBoundary
        fallback={
          <ItemCardSkeleton
            icon={<WarningIcon color="status.error" />}
            name={<i>{t`Could not load item`}</i>}
          />
        }
      >
        <Suspense fallback={<ItemCardSkeleton />}>
          <ItemCardLoader itemId={itemId} />
        </Suspense>
      </ErrorBoundary>
    );
  }

  return (
    <ItemCardSkeleton
      icon={<InfoIcon />}
      name={<i>{t`This item has been deleted`}</i>}
    />
  );
};

const ItemCardLoader = ({ itemId }: SelfLoadingItemCardProps) => {
  const { useLazyLoadQuery } = useSafeRelay();
  const data = useLazyLoadQuery<ItemCard_item_Query>(itemCardQuery, { itemId });
  if (!data.node) {
    return <ItemCardSkeleton name={t`Not found`} />;
  }
  return <PreloadedItemCard item={data.node} />;
};

const PreloadedItemCard: React.FC<PreloadedItemCardProps> = ({
  item: _item,
}) => {
  const item = useFragment(itemCardFragment, _item);

  switch (item.__typename) {
    case 'AnnouncementAttachment':
      return <AnnouncementAttachmentCard node={item} />;
    case 'Event':
      return <EventCard event={item} useCannonicalLink disableSpaceContext />;
    case 'Folder':
    case 'FolderItem':
      return <FolderItem node={item} viewMode="grid" />;
    case 'Space':
      return <SpaceCard space={item} />;
    case 'Announcement':
      return <MinimalAnnouncementCard announcement={item} />;
    default:
      return <ItemCardSkeleton />;
  }
};

interface ItemCardSkeletonProps {
  icon?: React.ReactNode;
  name?: React.ReactNode;
}

const ItemCardSkeleton = ({
  icon,
  name,
  ...linkProps
}: MaybeLinkProps<ItemCardSkeletonProps>) => {
  return (
    <LinkBox display="inline-block">
      <Card isNotClickable={!linkProps} display="inline-block">
        <HStack w="100%" py={1} pl={2} pr={3}>
          <Box fontSize="xl" color="blue.500">
            {icon || <SkeletonCircle size="10" />}
          </Box>
          {
            <LinkOverlay as={Link} {...linkProps}>
              <Text flex={0.7}>
                {name || <SkeletonText noOfLines={1} spacing="4" />}
              </Text>
            </LinkOverlay>
          }
        </HStack>
      </Card>
    </LinkBox>
  );
};
